import {
    fetchAssessmentFailure,
    fetchAssessmentRequest,
    fetchAssessmentSuccess,
    patchAssessmentFailure,
    patchAssessmentRequest,
    patchAssessmentSuccess,
    postAssessmentFailure,
    postAssessmentRequest,
    postAssessmentSuccess,
    fetchAssessmentScoresByApplicationRequest,
    fetchAssessmentScoresByApplicationSuccess,
    fetchAssessmentScoresByApplicationFailure,
    deleteAssessmentRequest,
    deleteAssessmentSuccess,
    deleteAssessmentFailure,
} from '@actions/assessments';
import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import { Assessment, AssessmentScoreResponse } from 'src/types/shared/Assessments';

interface AssessmentState {
    isFetching: boolean;
    fetchError: string | null;
    isPatching: boolean;
    patchSuccess: boolean;
    patchError: string | null;
    isPosting: boolean;
    postSuccess: boolean;
    postError: string | null;
    assessments: Record<number, Assessment>;
    applicationScores: AssessmentScoreResponse[];
}

const initialState: AssessmentState = {
    isFetching: false,
    fetchError: null,
    isPosting: false,
    postSuccess: false,
    postError: null,
    isPatching: false,
    patchSuccess: false,
    patchError: null,
    assessments: {},
    applicationScores: [],
};

export default createReducer(initialState, {
    [fetchAssessmentRequest.type]: handleFetchRequest,
    [fetchAssessmentFailure.type]: handleFetchFailure,
    [fetchAssessmentSuccess.type]: handleFetchAssessmentSuccess,
    [patchAssessmentRequest.type]: handlePatchRequest,
    [patchAssessmentFailure.type]: handlePatchFailure,
    [patchAssessmentSuccess.type]: handlePatchAssessmentSuccess,
    [postAssessmentRequest.type]: handlePostRequest,
    [postAssessmentFailure.type]: handlePostFailure,
    [postAssessmentSuccess.type]: handlePostAssessmentSuccess,
    [fetchAssessmentScoresByApplicationRequest.type]: handleFetchRequest,
    [fetchAssessmentScoresByApplicationSuccess.type]: handleFetchAssessmentScoresSuccess,
    [fetchAssessmentScoresByApplicationFailure.type]: handleFetchFailure,
    [deleteAssessmentRequest.type]: handlePatchRequest,
    [deleteAssessmentSuccess.type]: handleDeleteSuccess,
    [deleteAssessmentFailure.type]: handlePatchFailure,
});

function handleFetchRequest(state: AssessmentState) {
    state.isFetching = true;
    state.fetchError = null;
}

function handleFetchFailure(state: AssessmentState, action: PayloadAction<string>) {
    state.fetchError = action.payload;
    state.isFetching = false;
}

function handleFetchAssessmentSuccess(state: AssessmentState, action: PayloadAction<Assessment>) {
    state.assessments[action.payload.applicationID] = action.payload;
    state.isFetching = false;
}

function handleFetchAssessmentScoresSuccess(
    state: AssessmentState,
    action: PayloadAction<AssessmentScoreResponse[]>,
) {
    state.applicationScores = action.payload;
    state.isFetching = false;
}

function handlePatchRequest(state: AssessmentState) {
    state.isPatching = true;
    state.patchSuccess = false;
    state.patchError = null;
}

function handlePatchFailure(state: AssessmentState, action: PayloadAction<string>) {
    state.patchError = action.payload;
    state.isPatching = false;
}

function handlePatchAssessmentSuccess(state: AssessmentState, action: PayloadAction<Assessment>) {
    state.assessments[action.payload.applicationID] = action.payload;
    state.isPatching = false;
    state.patchSuccess = true;
}

function handleDeleteSuccess(
    state: AssessmentState,
    action: PayloadAction<{ assessmentID: number }>,
) {
    state.applicationScores = state.applicationScores.filter(
        x => x.assessment.id !== action.payload.assessmentID,
    );
    state.isPatching = false;
    state.patchSuccess = true;
}

function handlePostRequest(state: AssessmentState) {
    state.isPosting = true;
    state.patchSuccess = false;
    state.postSuccess = false;
    state.postError = null;
}

function handlePostFailure(state: AssessmentState, action: PayloadAction<string>) {
    state.postError = action.payload;
    state.isPosting = false;
}

function handlePostAssessmentSuccess(state: AssessmentState, action: PayloadAction<Assessment>) {
    state.assessments[action.payload.applicationID] = action.payload;
    state.isPosting = false;
    state.postSuccess = true;
}
